import { useEffect, useState } from "react";
import { ArrowsLeftRight, Lightning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import { fmtKg, fmtNum, fmtUsd } from "@/lib/format";

export default function Optimizer() {
  const { active } = useProjects();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!active) return;
    api.get(`/projects/${active.id}/boq`).then((r) => {
      setItems(r.data.items);
      if (r.data.items.length > 0) setSelected(r.data.items[0].id);
    });
  }, [active]);

  useEffect(() => {
    if (!active || !selected) return;
    api.post(`/projects/${active.id}/optimize`, { project_id: active.id, boq_item_id: selected }).then((r) => setResult(r.data));
  }, [active, selected]);

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Reduction Engine"
        title="Carbon Reduction Optimizer"
        subtitle="Compare each BOQ line against lower-carbon alternatives in the same category."
        guideId="optimizer"
      />

      <section className="px-8 pt-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="border border theme-border theme-surface p-6">
          <div className="overline">Choose line to optimise</div>
          <div className="font-serif-display text-2xl mb-3">BOQ items</div>
          <div className="space-y-1 max-h-[520px] overflow-y-auto">
            {items.map((i) => (
              <button
                key={i.id}
                data-testid={`opt-line-${i.id}`}
                onClick={() => setSelected(i.id)}
                className={`w-full text-left px-3 py-2 border rounded-sm transition-colors ${
                  selected === i.id ? "border-gold bg-gold/10" : "border theme-border hover:border-white/30"
                }`}
              >
                <div className="text-sm theme-ink truncate">{i.name}</div>
                <div className="flex items-baseline justify-between mt-1">
                  <div className="text-[11px] theme-ink-dim">{i.category} · {i.unit}</div>
                  <div className="text-xs font-mono-data text-gold">{fmtKg(i.total_kg)}</div>
                </div>
              </button>
            ))}
            {items.length === 0 && <div className="text-sm theme-ink-dim">No BOQ lines.</div>}
          </div>
        </div>

        <div className="xl:col-span-2 space-y-4">
          {result && (
            <>
              <div className="border border theme-border theme-surface p-6">
                <div className="overline">Current selection</div>
                <div className="font-serif-display text-2xl theme-ink mt-1">{result.current.name}</div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="overline">A1–A3 factor</div>
                    <div className="font-mono-data theme-ink text-base mt-1">{fmtNum(result.current.factor_a1a3, 1)} kg/{result.current.unit}</div>
                  </div>
                  <div>
                    <div className="overline">Quantity</div>
                    <div className="font-mono-data theme-ink text-base mt-1">{fmtNum(result.current.quantity)} {result.current.unit}</div>
                  </div>
                  <div>
                    <div className="overline">Total kgCO₂e</div>
                    <div className="font-mono-data text-gold text-base mt-1">{fmtKg(result.current.total_kg)}</div>
                  </div>
                </div>
              </div>

              <div className="border border theme-border theme-surface">
                <div className="px-6 py-4 border-b theme-border flex items-baseline justify-between">
                  <div>
                    <div className="overline">Alternatives</div>
                    <div className="font-serif-display text-xl">Lower-carbon options</div>
                  </div>
                  <ArrowsLeftRight size={16} className="text-gold" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                      <tr>
                        <th className="text-left px-6 py-3">Alternative</th>
                        <th className="text-right px-3 py-3">Factor</th>
                        <th className="text-right px-3 py-3">New Total</th>
                        <th className="text-right px-3 py-3">Savings (kg)</th>
                        <th className="text-right px-6 py-3">Savings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.alternatives.map((a) => (
                        <tr key={a.epd_id} className="border-t border-white/5 hover:bg-black/[0.02] dark:bg-white/[0.02]">
                          <td className="px-6 py-3">
                            <div className="theme-ink">{a.name}</div>
                            <div className="text-[11px] theme-ink-dim">{a.manufacturer}</div>
                          </td>
                          <td className="px-3 py-3 text-right font-mono-data theme-ink-muted">{fmtNum(a.factor_a1a3, 1)}</td>
                          <td className="px-3 py-3 text-right font-mono-data">{fmtKg(a.new_total_kg)}</td>
                          <td className={`px-3 py-3 text-right font-mono-data ${a.savings_kg > 0 ? "text-eco" : "text-risk"}`}>
                            {a.savings_kg > 0 ? "−" : "+"}{fmtKg(Math.abs(a.savings_kg))}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <div className={`font-mono-data ${a.savings_pct > 0 ? "text-eco" : "text-risk"}`}>
                              {a.savings_pct > 0 ? "−" : "+"}{Math.abs(a.savings_pct)}%
                            </div>
                            <div className="text-[11px] theme-ink-dim mt-0.5 flex items-center justify-end gap-1">
                              <Lightning size={10} weight="fill" className="text-gold" />
                              {fmtUsd(a.carbon_cost_savings_usd)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
      <div className="pb-12" />
    </div>
  );
}
